/* eslint-disable no-undef */
import {
  useContext, useState, useEffect, useRef, useMemo,
} from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import {
  Row, Col,
} from 'antd';

import Layout from 'components/layout';
import { HeadSEO } from 'components/base';
import { FilterHeader } from 'components/filter';
import { sorts } from 'utils/filters';
import { FilterPanel } from 'components/filterPanel';
import PaginatedGrid from 'components/paginatedGrid';
import ProductsGrid from 'components/productsGrid';
import Paginator, { getSeoRelLinks } from 'components/paginator';
import { baseTranslations, getCountryFromMarket } from 'utils/translations';
import { errorContext } from 'context/error';
import { userContext } from 'context/user';
import { getProducts } from 'actions/products';
import { getSeo } from 'actions/general';
import {
  filterTransformer, DEFAULT_LIMIT,
  getMarket, emptyGridImg,
  getRealID, updateQuerystringParam, userAgentIsMobile,
  COOKIES, getCookie, hasAnyVoucherApplied,
} from 'utils/helpers';
import { initCMS } from 'utils/cms';
import { IS_CAMPER, IS_NNORMAL } from 'utils/constants/system';
import { TIER_POSITIONS } from 'utils/constants/cms';
import { AUTO_BONDS } from 'utils/constants/cookies';
import ProductPlaceholder from 'components/productPlaceholder';

import styles from 'styles/ProductList.module.css';
import { useI18n } from 'context/i18n';

const TierComponent = IS_CAMPER ? dynamic(
  () => import('@camper/react-web-components').then((res) => res.ORGANISMS.TierComponent),
) : dynamic(
  () => import('@nnormal/react-web-components').then((res) => res.ORGANISMS.TierBannerComponent),
);

const GridSeoBlocks = dynamic(
  () => import('components/gridSeoBlocks').then((mod) => mod.GridSeoBlocks),
);

const DEFAULT_SORT = Object.keys(sorts)[0];

export default function ProductsListBrand({
  firstProducts, total, availableFilters, baseAvailable, locale, initialPage,
  appliedFilters, filtersCount, footerProps, tiersProps, tnmProps, breadcrumbI18n,
  gtmProps, currentList, ribbon, camperlab, title, description, metatags,
  isMobile, autoappliedVoucher, urlCanonical, hasFiltersInURL, originalCta, seoContent, recycled,
  subgridProducts, subgridUrlCollection,
}) {
  const { spreadError } = useContext(errorContext);
  const {
    readyForVouchers, setContextFilters, contextFilters, activatedPrevouchers, handleActivatePrevoucher,
  } = useContext(userContext);
  const { t } = useI18n();
  const [ shouldFetch, setShouldFetch ] = useState(false);
  // para prevenir que cargue mas productos en la primera carga (es decir si entrase inview el interseccion observer de arriba o abajo);
  const [ productsPerPage, setProductsPerPage ] = useState({
    [initialPage]: firstProducts,
  });
  const [ currentFilter, setCurrentFilter ] = useState(appliedFilters);
  const [ currentAvailableFilters, setCurrentAvailableFilters ] = useState(availableFilters || {});
  const [ currentSort, setCurrentSort ] = useState(DEFAULT_SORT);
  const [ currentTotal, setCurrentTotal ] = useState(total);
  const [ currentFiltersCount, setCurrentFiltersCount ] = useState(filtersCount);
  const [ hasFiltersUrl, setHasFiltersUrl ] = useState(hasFiltersInURL);
  const [ mobileFilterPanelVisible, setMobileFilterPanelVisible ] = useState(false);
  const [ filterPanelVisible, setFilterPanelVisible ] = useState(IS_CAMPER && !camperlab);
  const [ loading, setLoading ] = useState(false);
  const [ loadingNextPage, setLoadingNextPage ] = useState(false);
  const [ recalculatingPrices, setRecalculatingPrices ] = useState(false);
  const [ voucherApplied, setVoucherApplied ] = useState(false);
  const [ inViewPage, setInViewPage ] = useState(initialPage);
  const [ numPlaceholders, setNumPlaceholders ] = useState(DEFAULT_LIMIT);

  const router = useRouter();
  const { query } = router;
  const { target, product, brand } = query;

  const market = getMarket(locale);
  const country = getCountryFromMarket(market);
  const typologyFilterValues = currentFilter?.typologies?.map((item) => item.value) ?? [];

  const initialCamperlabFilters = useRef(null);

  useEffect(() => {
    setContextFilters(currentFilter);
  }, [ currentFilter ]);

  const handleGetProducts = (page, filters, sort, limit, callback) => {
    const newFiltersCount = Object.keys(filters).length > 0 ? Object.keys(filters).filter((item) => item !== 'first').reduce((a, b) => filters[b].length + a, 0) : 0;
    const queryFilters = filterTransformer.toQuery(filters);

    const autoVoucher = getCookie(COOKIES.AUTO_BONDS) || '';
    const voucher = getCookie(COOKIES.BONDS) || '';
    const voucherDiscount = IS_CAMPER && autoVoucher && autoVoucher === AUTO_BONDS.APPLY;
    getProducts({
      lang: locale,
      target,
      page,
      filters: queryFilters,
      limit,
      sort,
      product,
      voucherDiscount: voucherDiscount ? voucher : undefined,
    }).then(({ products: responseProducts, total: responseTotal, availableFilters: newAvailableFilters }) => {
      setCurrentFiltersCount(newFiltersCount);
      const url = window.location.href;
      const hasFilterInUrl = url.includes('?filter.') || url.includes('&filter.');
      setHasFiltersUrl(hasFilterInUrl);
      setCurrentFilter(filters);
      setCurrentAvailableFilters(newAvailableFilters);
      setCurrentSort(sort);
      return callback(responseProducts, responseTotal);
    }).catch(spreadError);
  };

  const handleLoadNextPage = (nextPageNumber) => {
    const currentLoadedPages = Object.keys(productsPerPage);
    if (shouldFetch && nextPageNumber >= 1 && !currentLoadedPages.includes(String(nextPageNumber))) {
      const nextPageOffset = nextPageNumber > 1 ? (nextPageNumber - 1) * DEFAULT_LIMIT : 0;
      const totalPages = Math.ceil(currentTotal / DEFAULT_LIMIT);
      if (nextPageOffset < currentTotal && !loadingNextPage) {
        let lastItem = document.querySelectorAll('.grid > div:last-child');
        if (lastItem.length) {
          [ lastItem ] = lastItem;
        }
        window.currentScrollGrid = window.scrollY;
        setLoadingNextPage(true);
        if (nextPageNumber === totalPages) {
          const remainingProducts = currentTotal - (DEFAULT_LIMIT * (nextPageNumber - 1));
          setNumPlaceholders(remainingProducts);
        } else {
          setNumPlaceholders(DEFAULT_LIMIT);
        }
        handleGetProducts(nextPageOffset, currentFilter, currentSort, DEFAULT_LIMIT, (response) => {
          const newProductsPerPage = JSON.parse(JSON.stringify(productsPerPage));
          newProductsPerPage[nextPageNumber] = response;
          setProductsPerPage(newProductsPerPage);
          setInViewPage(nextPageNumber);
          setLoadingNextPage(false);
          if (window.scrollY > window.currentScrollGrid && lastItem) {
            lastItem.nextElementSibling?.nextElementSibling?.scrollIntoView();
          }
        });
      }
    }
  };

  const pushToUrl = (search, sort) => {
    let querystring = '?';
    if (Object.keys(search).length > 0) {
      const filtersQuerystring = Object.keys(search).map((filterKey) => `${filterKey}=${encodeURI(search[filterKey])}`);
      querystring = `${querystring}${filtersQuerystring.join('&')}`;
    }
    updateQuerystringParam(querystring, 'sort', sort, DEFAULT_SORT);
  };

  const handleApplyFilters = (filters, sort = DEFAULT_SORT) => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }

    const filtersToUse = camperlab && initialCamperlabFilters.current !== null ?
      {
        ...JSON.parse(JSON.stringify(filters)),
        ...JSON.parse(JSON.stringify(initialCamperlabFilters.current)),
      } : filters;

    setLoading(true);
    const queryFilters = filterTransformer.toQuery(filtersToUse);
    handleGetProducts(0, filtersToUse, sort, DEFAULT_LIMIT, (response, newTotal) => {
      setProductsPerPage({ 1: response });
      pushToUrl(queryFilters, sort);
      setCurrentTotal(newTotal);
      setLoading(false);
    });
  };

  const handleMobileOnClickFilterBtn = (visible, fakeApply = false) => {
    document.querySelector('body').classList.toggle('filterOpen');
    setMobileFilterPanelVisible(visible);
    if (fakeApply) {
      setLoading(true);
      setTimeout(() => setLoading(false), 500);
    }
  };
  const handleOnClickFilterBtn = (visible, fakeApply = false) => {
    setFilterPanelVisible(visible);
    if (fakeApply) {
      setLoading(true);
      setTimeout(() => setLoading(false), 500);
    }
  };

  useEffect(() => {
    if (window && window.history) {
      window.history.scrollRestoration = 'manual';
    }

    if (camperlab) {
      initialCamperlabFilters.current = JSON.parse(JSON.stringify(appliedFilters));
    }

    if (initialPage > 1) {
      const scrollY = document.querySelector('#__next').offsetHeight * 0.3;
      window.scrollTo(0, scrollY);
      // aplicamos un 30% de scroll vertical aprox para que cuando se haga scroll hacia arriba el inviewTop haga trigger
    }
    setShouldFetch(true);

    // cambios de feat/7019 que no se choque con los cambios de feat/retain-scroll
    localStorage.setItem('gridTarget', target);
    localStorage.setItem('gridProduct', product);
    localStorage.setItem('gridBrand', brand);
  }, []);

  useEffect(() => {
    if (readyForVouchers === true) {
      const cta = document.getElementsByClassName('subclaimcta');

      const appliedVoucher = hasAnyVoucherApplied(firstProducts, t, locale, contextFilters, activatedPrevouchers, handleActivatePrevoucher);

      if (IS_CAMPER && appliedVoucher !== false) {
        setVoucherApplied(true);
        setTimeout(() => {
          if (cta.length) {
            cta[0].style.visibility = 'visible';
            cta[0].style.color = '#07b323';
            cta[0].style.background = 'unset';
            cta[0].style.backgroundSize = 'unset';
            cta[0].style.animation = 'unset';
            cta[0].style.textDecoration = 'unset';
            cta[0].style.pointerEvents = 'none';
            cta[0].text = t('listado.productos', 'discount.applied.BF22');
          }
        }, 1000);
      } else {
        setTimeout(() => {
          if (cta.length) {
            cta[0].style.visibility = 'visible';
            cta[0].style.color = originalCta.fontColor;
            cta[0].style.background = 'unset';
            cta[0].style.backgroundSize = 'unset';
            cta[0].style.animation = 'unset';
            cta[0].text = originalCta.text;
          }
        }, 1000);
      }
    }
  }, [ readyForVouchers ]);

  useEffect(() => {
    if (IS_CAMPER && autoappliedVoucher) {
      setRecalculatingPrices(true);
      setTimeout(() => {
        handleGetProducts(0, currentFilter, currentSort, DEFAULT_LIMIT, (response) => {
          const newProductsPerPage = JSON.parse(JSON.stringify(productsPerPage));
          newProductsPerPage[1] = response;
          setProductsPerPage(newProductsPerPage);
          setRecalculatingPrices(false);
          setVoucherApplied(true);
        }, false);
      }, 1000);
    }
  }, [ autoappliedVoucher ]);

  const tiersInGrid = !!tiersProps[TIER_POSITIONS.GRID];
  const tiersIndexes = tiersInGrid ? tiersProps?.[TIER_POSITIONS.GRID]?.flatMap((tier) => tier.positionInGrid.map((pos) => pos)).sort((a, b) => a - b) : [];
  const hasStandalone = tiersProps[TIER_POSITIONS.STANDALONE] !== null && tiersProps[TIER_POSITIONS.STANDALONE] !== undefined;
  const hasTitleDescription = title !== null && title !== undefined && description !== null && description !== undefined;
  const totalPages = Math.ceil(total / DEFAULT_LIMIT);
  const isGridSeeAll = currentFiltersCount === 0 && !hasFiltersUrl;
  const seoLinks = getSeoRelLinks(initialPage, totalPages, urlCanonical, isGridSeeAll);
  const filterPanelClassname = subgridProducts.length === 0 ? styles.filterPanelCol : styles.filterPanelColStatic;

  const pagesRange = useMemo(() => {
    const counted = Object.fromEntries(Object.entries(productsPerPage).reduce((acc, [ page, products ]) => {
      const min = !acc.length ? 0 : acc[acc.length - 1][1][1] + 1;
      let max = min + products.length;
      tiersIndexes.forEach((tIndex) => {
        if (tIndex > min && tIndex <= max) {
          max = max + 1;
        }
      });
      acc.push([ page, [ min, max ] ]);
      return acc;
    }, []));
    return counted;
  }, [ productsPerPage, tiersIndexes ]);

  // Funciones de filtros
  const handleClickSort = (sortValue) => {
    dataLayerHandleEvent({
      event: 'eventGA',
      eventCat: 'ecommerce',
      eventAct: 'gridSort',
      eventLbl: sortValue,
    });
    handleApplyFilters(appliedFilters, sortValue);
  };

  const handleRemoveFilter = (id, option) => {
    const currentFilters = { ...appliedFilters };

    let indexOfFilter = null;

    currentFilters[id].forEach((item, index) => {
      const { key } = option;
      if (key === item.key) {
        indexOfFilter = index;
      }
    });

    if (indexOfFilter === null) {
      throw new Error('FATAL: Index for added filter not found.');
    }

    currentFilters[id].splice(indexOfFilter, 1);
    if (currentFilters[id].length === 0) {
      delete currentFilters[id];
    }
    if (Object.keys(currentFilters).length === 1 && Object.keys(currentFilters)[0] === 'first') {
      delete currentFilters.first;
    }

    handleApplyFilters(currentFilters, currentSort);
  };

  return (
    <Layout
      footerProps={footerProps}
      tnmProps={tnmProps}
      breadcrumbs
      breadcrumbsBottom={hasStandalone || hasTitleDescription}
      ribbon={ribbon}
      camperlab={camperlab}
      recycled={recycled}
      scrollArrow={true}
      breadcrumbI18n={breadcrumbI18n}
      pageName={'grid'}
    >
      <div>
        <HeadSEO
          title={metatags?.titlePage}
          description={metatags?.metaDescription}
          pageName={'grid'}
          gtmProps={gtmProps}
        >
          {seoLinks}
        </HeadSEO>
        <Row>
          <Col span={24} className={styles.productListTitle}>
            {tiersProps[TIER_POSITIONS.STANDALONE] ? (
              <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE]} typology={typologyFilterValues} />
            ) : (
              <>
                <h1 className={styles.title} dangerouslySetInnerHTML={{ __html: title }} />
                <h2 className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
              </>
            )}
          </Col>
          {IS_CAMPER ? (
            <>
              {/* Mobile components */}
              <Col span={24} className={styles.filterHeaderCol} id="mobile-plp-filter-nav" data-mobile>
              <FilterHeader
                open={mobileFilterPanelVisible}
                toggle={() => handleMobileOnClickFilterBtn(!mobileFilterPanelVisible)}
                count={currentFiltersCount}
                filters={appliedFilters}
                onRemoveFilter={handleRemoveFilter}
                onReset={() => handleApplyFilters({}, currentSort)}
                total={currentTotal}
                onSort={handleClickSort}
                currentSort={currentSort}
                hiddenFilters={[ 'first' ]}
              />
              </Col>
              <Col
                xs={{ span: mobileFilterPanelVisible ? 24 : 0 }}
                md={{ span: mobileFilterPanelVisible ? 8 : 0 }}
                lg={{ span: mobileFilterPanelVisible ? 8 : 0 }}
                xl={{ span: mobileFilterPanelVisible ? 8 : 0 }}
                xxl={{ span: mobileFilterPanelVisible ? 6 : 0 }}
                className={filterPanelClassname}
                id="plp-filter-panel"
                data-open={mobileFilterPanelVisible}
                data-mobile

              >
                <FilterPanel
                  appliedFilters={currentFilter}
                  availableFilters={currentAvailableFilters}
                  currentSort={currentSort}
                  onApply={handleApplyFilters}
                  total={currentTotal}
                  onClickCloseBtn={handleMobileOnClickFilterBtn}
                  baseAvailable={baseAvailable}
                  filtersCount={currentFiltersCount}
                  country={country}
                  camperlab={camperlab}
                />
              </Col>
              {/* desktop components */}
              <Col span={24} className={styles.filterHeaderCol} id="plp-filter-nav" data-desktop>
                <FilterHeader
                  open={filterPanelVisible}
                  toggle={() => handleOnClickFilterBtn(!filterPanelVisible)}
                  count={currentFiltersCount}
                  filters={currentFilter}
                  onRemoveFilter={handleRemoveFilter}
                  onReset={() => handleApplyFilters({}, currentSort)}
                  total={currentTotal}
                  onSort={handleClickSort}
                  currentSort={currentSort}
                  hiddenFilters={[ 'first' ]}
                />
              </Col>
              <Col
                xs={{ span: filterPanelVisible ? 24 : 0 }}
                md={{ span: filterPanelVisible ? 8 : 0 }}
                lg={{ span: filterPanelVisible ? 8 : 0 }}
                xl={{ span: filterPanelVisible ? 8 : 0 }}
                xxl={{ span: filterPanelVisible ? 6 : 0 }}
                className={filterPanelClassname}
                id="plp-filter-panel"
                data-open={filterPanelVisible}
              >
                <FilterPanel
                  appliedFilters={currentFilter}
                  availableFilters={currentAvailableFilters}
                  currentSort={currentSort}
                  onApply={handleApplyFilters}
                  total={currentTotal}
                  onClickCloseBtn={handleOnClickFilterBtn}
                  baseAvailable={baseAvailable}
                  filtersCount={currentFiltersCount}
                  country={country}
                  camperlab={camperlab}
                />
              </Col>
            </>
          ) : null}
          <Col
            className={styles.gridColWrapper}
            xs={{ span: 24 }}
            md={{ span: filterPanelVisible ? 16 : 24 }}
            lg={{ span: filterPanelVisible ? 16 : 24 }}
            xl={{ span: filterPanelVisible ? 16 : 24 }}
            xxl={{ span: filterPanelVisible ? 18 : 24 }}
          >
            {IS_NNORMAL ? <h1 className={styles.gridTitle}>{t('listado.productos', `grid.title.${breadcrumbI18n.target}.${breadcrumbI18n.family}`, 'Todos los zapatos')}</h1> : null}
            <Row className={`${styles.rowGrid} grid`}>
              {loading ?
                Array(DEFAULT_LIMIT).fill().map((n, i) => (
                  <Col
                    xs={{ span: 12 }}
                    md={{ span: filterPanelVisible ? 12 : 8 }}
                    lg={{ span: filterPanelVisible ? 12 : 8 }}
                    xl={{ span: filterPanelVisible ? 12 : 8 }}
                    xxl={{ span: filterPanelVisible ? 8 : 6 }}
                    key={`${i}f`}
                  >
                    <ProductPlaceholder />
                  </Col>
                )) :
                Object.keys(productsPerPage).map((pageKey) => {
                  const page = Number(pageKey);

                  const pageMin = pagesRange[pageKey][0];
                  const pageMax = pagesRange[pageKey][1];

                  const tiersInRange = tiersIndexes.filter((i) => i >= pageMin && i < pageMax);

                  // Si la pagina tiene menos de 40 productos y hay un tier en ese rango hay que meter el primero en la pagina
                  if (pageMax - pageMin < DEFAULT_LIMIT && tiersIndexes.some((i) => i > pageMax && i >= pageMin && i <= pageMin + DEFAULT_LIMIT)) {
                    // como el tiersIndexes esta ya ordenado con encontrar el primero que este en el rango ya es suficiente
                    const nextTier = tiersIndexes.find((i) => i > pageMax && i >= pageMin && i <= pageMin + DEFAULT_LIMIT);
                    if (nextTier) {
                      tiersInRange.push(nextTier);
                    }
                  }

                  let productsCopy = JSON.parse(JSON.stringify(productsPerPage[page]));
                  if (page === 1 && tiersProps[TIER_POSITIONS.STANDALONE_LEFT]) {
                    const subTierProps = tiersProps[TIER_POSITIONS.STANDALONE_LEFT];
                    const subTierObject = {
                      isTier: true,
                      isPattersonTier: true,
                      tierType: TIER_POSITIONS.STANDALONE_LEFT,
                      tierSize: {
                        xs: { span: 24 },
                        md: { span: filterPanelVisible ? 24 : 16 },
                        lg: { span: filterPanelVisible ? 24 : 16 },
                        xl: { span: filterPanelVisible ? 24 : 16 },
                        xxl: { span: filterPanelVisible ? 16 : 12 },
                      },
                      renderTier: <TierComponent {...subTierProps}/>,
                    };
                    productsCopy = [ subTierObject, ...productsCopy ];
                  }
                  if (tiersInRange.length > 0) {
                    tiersInRange.forEach((tIndex) => {
                      const tierToUse = tiersProps[TIER_POSITIONS.GRID].find((tier) => tier.positionInGrid.includes(tIndex));
                      if (!tierToUse) {
                        return;
                      }
                      const { numColumns, numColumnsTablet, numColumnsMobile } = tierToUse;
                      let tierSize = {};
                      if (!Number.isNaN(Number(numColumns))) tierSize.lg = 6 * numColumns;
                      if (!Number.isNaN(Number(numColumnsTablet))) tierSize.md = 8 * numColumnsTablet;
                      if (!Number.isNaN(Number(numColumnsMobile))) tierSize.xs = 12 * numColumnsMobile;
                      if (Object.keys(tierSize).length === 0) tierSize = undefined;
                      const tierGrid =
                        {
                          isTier: true,
                          isPattersonTier: true,
                          tierImg: emptyGridImg,
                          tierSize,
                          renderTier: <TierComponent {...tierToUse} positionInGrid={tIndex} />,
                        };

                      const fitsInGrid = tIndex <= pageMax;
                      const pageOffset = -2 + page;

                      const indexToInsert = tIndex - pageMin + pageOffset;
                      if (fitsInGrid) {
                        productsCopy = [ ...productsCopy.slice(0, indexToInsert), tierGrid, ...productsCopy.slice(indexToInsert) ];
                      } else if (!productsCopy[productsCopy.length - 1]?.isTier) {
                        productsCopy = [ ...productsCopy, tierGrid ];
                      }
                    });
                  }
                  return (
                    <PaginatedGrid
                      key={pageKey}
                      page={page}
                      products={productsCopy}
                      currentList={currentList}
                      target={target}
                      product={product}
                      locale={locale}
                      colXS={{ span: 12 }}
                      colMD={{ span: filterPanelVisible ? 12 : 8 }}
                      colLG={{ span: filterPanelVisible ? 8 : 6 }}
                      colXL={{ span: filterPanelVisible ? 8 : 6 }}
                      colXXL={{ span: filterPanelVisible ? 8 : 6 }}
                      handleLoadNextPage={handleLoadNextPage}
                      camperlab={camperlab}
                      withHover={!isMobile}
                      withLabel
                      recalculating={recalculatingPrices}
                      voucherApplied={voucherApplied}
                      loadingNextPage={loadingNextPage}
                      currentListName={title}
                    />
                  );
                })
              }
              {loadingNextPage &&
                Array(numPlaceholders).fill().map((n, i) => (
                  <Col
                    xs={{ span: 12 }}
                    md={{ span: filterPanelVisible ? 12 : 8 }}
                    lg={{ span: filterPanelVisible ? 8 : 6 }}
                    xl={{ span: filterPanelVisible ? 6 : 6 }}
                    xxl={{ span: filterPanelVisible ? 8 : 6 }}
                    key={`${i}ph`}
                  >
                    <ProductPlaceholder />
                  </Col>
                ))
              }
            </Row>
          </Col>
        </Row>
        <Row>
          {tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM] &&
            <Col span={24} className={styles.productListTitle}>
              <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM]}/>
            </Col>
          }
          {IS_CAMPER && subgridProducts.length > 0 &&
            <Row
              className={`${styles.rowGrid} ${styles.rowSubgrid} grid`}
              style={{
                '--background': 'var(--color-subgrid)',
                'background-color': 'var(--color-subgrid)',
              }}
            >
              <Col span={24}>
                <div className={styles.subgridTitle} dangerouslySetInnerHTML={{ __html: t('listado.productos', 'subgrid.title', 'check <i>these</i> out:') }} />
              </Col>
              <ProductsGrid
                origin='grid'
                products={subgridProducts}
                locale={locale}
                colXS={{ span: 12 }}
                colMD={{ span: 8 }}
                colLG={{ span: 6 }}
                colXL={{ span: 6 }}
                colXXL={{ span: 6 }}
                withHover
                withSKU
                withLabel={true}
                currentList='subgrid-products'
                camperlab={false}
                currentListName='subgrid-products'
                recalculating={false}
                voucherApplied={false}
                withModal={false}
              />
              <Col span={24} className={styles.discoverMoreCol}>
                <a
                  className={styles.btnDiscoverMore}
                  href={subgridUrlCollection}
                >
                  {t('listado.productos', 'subgrid.discover.more', 'Discover more')}
                </a>
              </Col>
            </Row>
          }
        </Row>
        {IS_CAMPER && seoContent !== null ? <GridSeoBlocks content={seoContent} camperlab={camperlab} /> : null}
        {totalPages > 0 && isGridSeeAll &&
          <div className={filtersCount === 0 && inViewPage !== totalPages ? styles.paginatorDiv : `${styles.paginatorDiv} ${styles.hidden}`}>
            <Paginator totalPages={totalPages} currentPage={inViewPage} url={urlCanonical} locale={locale}/>
          </div>
        }
      </div>
    </Layout>
  );
}

ProductsListBrand.propTypes = {
  firstProducts: PropTypes.array,
  total: PropTypes.number,
  availableFilters: PropTypes.object,
  baseAvailable: PropTypes.object,
  locale: PropTypes.string,
  defaultLocale: PropTypes.string,
  datetime: PropTypes.string,
  uuid: PropTypes.string,
  appliedFilters: PropTypes.object,
  filtersCount: PropTypes.number,
  hasFiltersInURL: PropTypes.bool,
  footerProps: PropTypes.any,
  tnmProps: PropTypes.any,
  gtmProps: PropTypes.object,
  tiersProps: PropTypes.object,
  initialPage: PropTypes.number,
  currentList: PropTypes.string,
  ribbon: PropTypes.any,
  camperlab: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
  metatags: PropTypes.object,
  isMobile: PropTypes.bool,
  breadcrumbI18n: PropTypes.object,
  autoappliedVoucher: PropTypes.bool,
  urlCanonical: PropTypes.string,
  originalCta: PropTypes.object,
  resolvedUrl: PropTypes.string,
  seoContent: PropTypes.object,
  recycled: PropTypes.bool,
  subgridProducts: PropTypes.array,
  subgridUrlCollection: PropTypes.string,
};

ProductsListBrand.i18nPage = 'grid';

export async function getServerSideProps(context) {
  const lang = context.locale;
  const {
    target, product, brand, page, sort,
  } = context.query;

  const targets = Object.keys(baseTranslations.target).map((langCode) => baseTranslations.target[langCode]).reduce((a, b) => a.concat(b));
  const families = Object.keys(baseTranslations.product).map((langCode) => baseTranslations.product[langCode]).reduce((a, b) => a.concat(b));

  if (!targets.includes(target) || !families.includes(product)) {
    return {
      notFound: true,
    };
  }

  const searchParams = { ...context.query };
  delete searchParams.target;
  delete searchParams.product;

  const filters = {};

  Object.keys(searchParams).forEach((searchKey) => {
    if (searchKey.startsWith('filter.')) {
      filters[searchKey] = searchParams[searchKey];
    }
  });

  const hasFiltersInURL = Object.keys(filters).length > 0;
  filters['filter.lineCollection'] = brand.toUpperCase();

  Object.keys(searchParams).forEach((searchKey) => {
    if (searchKey.startsWith('filter.')) {
      filters[searchKey] = searchParams[searchKey];
    }
  });

  const isMobile = userAgentIsMobile(context);
  const resolvedUrl = context.resolvedUrl || '';
  try {
    const targetRealID = getRealID('target', target);
    const familyRealID = getRealID('product', product);

    const pageNumber = Number(page);
    const pageOffset = pageNumber > 1 ? (pageNumber - 1) * DEFAULT_LIMIT : 0;
    const appliedSort = sort && sort !== DEFAULT_SORT ? sort : false;
    const response = await getProducts({
      context, lang, target, page: pageOffset, filters, sort: appliedSort, isMobile, product, seoContent: true,
    });
    const {
      products, total, availableFilters, currentList, camperlab, title, description, urlCanonical, seoContent,
      rewalk, relatedCollectionId, urlRelatedCollection,
    } = response;
    const {
      tnmProps, footerProps, tiers, ribbon, cmsSeoContent,
    } = await initCMS({
      context,
      locale: lang,
      tierParams: [
        'collection',
        [ brand ],
        targetRealID,
        familyRealID,
      ],
      tnmSelectedTarget: targetRealID,
      seoContent: camperlab ? null : { target: targetRealID },
    });

    // No devolver un 404, ya que aunque la grid esté vacía es posible que haya Tiers
    /* if (products.length === 0) {
      return {
        notFound: true,
      };
    } */
    const baseAvailable = availableFilters;

    const appliedFilters = Object.keys(filters).length > 0 ? filterTransformer.decode(filters, availableFilters) : {};
    const filtersCount = Object.keys(appliedFilters).length > 0 ? Object.keys(appliedFilters).reduce((a, b) => appliedFilters[b].length + a, 0) : 0;

    const gtmProps = {
      target, product, targetRealID, familyRealID, products, currentList: currentList ?? null, camperlab,
    };

    const queryParams = [
      `profile=${lang}`,
      `family=${familyRealID || '1'}`,
      `gender=${targetRealID || 'X'}`,
      `option=${targetRealID || 'X'}`,
      'page=PRODUCTOS',
    ];
    const collectionFilter = filters['filter.collection'];
    const conceptFilter = filters['filter.concept'];
    const typologyFilter = filters['filter.typology'];
    const sustainabilityFilter = filters['filter.sustainability'];
    if (collectionFilter) {
      queryParams.push(`specialCollection=${collectionFilter}`);
    }
    if (conceptFilter) {
      queryParams.push(`collection=${conceptFilter}`);
    }
    if (typologyFilter) {
      queryParams.push(`typology=${typologyFilter}`);
    }
    if (sustainabilityFilter) {
      queryParams.push(`sustainability=${sustainabilityFilter}`);
    }
    const hasSubgrid = relatedCollectionId !== null && relatedCollectionId !== undefined && relatedCollectionId !== '';
    let subgridUrlCollection = '';
    let subgridProducts = [];
    if (hasSubgrid) {
      const resolvedUrlWithoutQuery = resolvedUrl.split('?')[0];
      subgridUrlCollection = urlRelatedCollection || `/${lang}${resolvedUrlWithoutQuery}?filter.collection=${relatedCollectionId}`;
      const subgridResponse = await getProducts({
        context, lang, target, page: 0, limit: 8, filters: { 'filter.collection': relatedCollectionId }, sort: false, product, isMobile, seoContent: true,
      });
      subgridProducts = subgridResponse.products.slice(0, 8);
    }
    const metatags = await getSeo(context, queryParams);

    let filterBrand = filters['filter.lineCollection'].toLowerCase();
    if (filters['filter.concept']) {
      filterBrand = `${filters['filter.concept'].toLowerCase()}`;
    }
    if (filters['filter.collection']) {
      filterBrand = `collection${filters['filter.collection'].toLowerCase()}`;
    }
    if (filters['filter.typology']) {
      filterBrand = `tipologiaconsumidores${filters['filter.typology'].replace(/_/g, '.').toLowerCase()}`;
    }
    const breadcrumbI18n = {
      target: targetRealID.toLocaleLowerCase() || null,
      family: familyRealID || null,
    };
    if ('collection' in appliedFilters || 'typology' in appliedFilters || 'concept' in appliedFilters || 'lineCollection' in appliedFilters) {
      breadcrumbI18n.brand = filterBrand;
    }

    const originalCta = {};
    if (tiers?.standalone && tiers?.standalone?.ctaText) {
      originalCta.text = tiers.standalone.ctaText;
      if (tiers.standalone.ctaTextProps) {
        originalCta.fontColor = tiers.standalone.ctaTextProps.fontColor;
        tiers.standalone.ctaTextProps.fontColor = '#ffffff00';
      }
    }

    return {
      props: {
        currentList: currentList || null,
        firstProducts: products,
        total,
        availableFilters,
        baseAvailable,
        appliedFilters,
        filtersCount,
        hasFiltersInURL,
        locale: lang,
        defaultLocale: lang,
        footerProps,
        tnmProps,
        gtmProps,
        tiersProps: tiers || {},
        initialPage: pageNumber || 1,
        ribbon,
        camperlab,
        title: title || null,
        description: description || null,
        metatags,
        isMobile,
        breadcrumbI18n,
        urlCanonical,
        originalCta,
        resolvedUrl,
        seoContent: seoContent ? {
          ...seoContent,
          cms: cmsSeoContent ? {
            ...cmsSeoContent,
            gridName: currentList ?? title ?? null,
          } : null,
        } : null,
        recycled: rewalk ?? false,
        subgridProducts,
        subgridUrlCollection,
      },
    };
  } catch (e) {
    console.error(e);
    context.res.statusCode = 500;
    return {
      props: {
        error: `${context.resolvedUrl} SSR Error: ${e}`,
        permanent: false,
      },
    };
  }
}
